import merge from 'lodash/merge';
import {
  deleteAccount as deleteAccountApi,
  updateShowAtMarketPlace as updateShowAtMarketPlaceApi,
} from '../../util/api';
import { updateIntercomUser } from '../../util/intercom';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess, clearCurrentUser, fetchCurrentUser } from '../../ducks/user.duck';
import { logoutSuccess, userLogout } from '../../ducks/Auth.duck';

// ================ Action types ================ //

export const SAVE_CONTACT_DETAILS_REQUEST = 'app/AccountSettingsPage/SAVE_CONTACT_DETAILS_REQUEST';
export const SAVE_CONTACT_DETAILS_SUCCESS = 'app/AccountSettingsPage/SAVE_CONTACT_DETAILS_SUCCESS';
export const SAVE_EMAIL_ERROR = 'app/AccountSettingsPage/SAVE_EMAIL_ERROR';
export const SAVE_PHONE_NUMBER_ERROR = 'app/AccountSettingsPage/SAVE_PHONE_NUMBER_ERROR';
export const SAVE_CONTACT_DETAILS_CLEAR = 'app/AccountSettingsPage/SAVE_CONTACT_DETAILS_CLEAR';

export const CHANGE_PASSWORD_REQUEST = 'app/AccountSettingsPage/CHANGE_PASSWORD_REQUEST';
export const CHANGE_PASSWORD_SUCCESS = 'app/AccountSettingsPage/CHANGE_PASSWORD_SUCCESS';
export const CHANGE_PASSWORD_ERROR = 'app/AccountSettingsPage/CHANGE_PASSWORD_ERROR';
export const CHANGE_PASSWORD_CLEAR = 'app/AccountSettingsPage/CHANGE_PASSWORD_CLEAR';

export const RESET_PASSWORD_REQUEST = 'app/AccountSettingsPage/RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'app/AccountSettingsPage/RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_ERROR = 'app/AccountSettingsPage/RESET_PASSWORD_ERROR';

export const DELETE_ACCOUNT_REQUEST = 'app/AccountSettingsPage/DELETE_ACCOUNT_REQUEST';
export const DELETE_ACCOUNT_SUCCESS = 'app/AccountSettingsPage/DELETE_ACCOUNT_SUCCESS';
export const DELETE_ACCOUNT_ERROR = 'app/AccountSettingsPage/DELETE_ACCOUNT_ERROR';

export const SAVE_LINKS_REQUEST = 'app/AccountSettingsPage/SAVE_LINKS_REQUEST';
export const SAVE_LINKS_SUCCESS = 'app/AccountSettingsPage/SAVE_LINKS_SUCCESS';
export const SAVE_LINKS_ERROR = 'app/AccountSettingsPage/SAVE_LINKS_ERROR';
export const SAVE_LINKS_CLEAR = 'app/AccountSettingsPage/SAVE_LINKS_CLEAR';

export const SAVE_VISIBILITY_REQUEST = 'app/AccountSettingsPage/SAVE_VISIBILITY_REQUEST';
export const SAVE_VISIBILITY_SUCCESS = 'app/AccountSettingsPage/SAVE_VISIBILITY_SUCCESS';
export const SAVE_VISIBILITY_ERROR = 'app/AccountSettingsPage/SAVE_VISIBILITY_ERROR';
export const SAVE_VISIBILITY_CLEAR = 'app/AccountSettingsPage/SAVE_VISIBILITY_CLEAR';

// ================ Reducer ================ //

const initialState = {
  saveEmailError: null,
  savePhoneNumberError: null,
  saveContactDetailsInProgress: false,
  contactDetailsChanged: false,

  changePasswordError: null,
  changePasswordInProgress: false,
  passwordChanged: false,

  resetPasswordInProgress: false,
  resetPasswordError: null,

  deleteAccountInProgress: false,
  deleteAccountError: null,

  saveLinksInProgress: false,
  saveLinksError: null,
  linksSaved: false,

  saveVisibilityInProgress: false,
  saveVisibilityError: null,
  visibilitySaved: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_CONTACT_DETAILS_REQUEST:
      return {
        ...state,
        saveContactDetailsInProgress: true,
        saveEmailError: null,
        savePhoneNumberError: null,
        contactDetailsChanged: false,
      };
    case SAVE_CONTACT_DETAILS_SUCCESS:
      return { ...state, saveContactDetailsInProgress: false, contactDetailsChanged: true };
    case SAVE_EMAIL_ERROR:
      return { ...state, saveContactDetailsInProgress: false, saveEmailError: payload };
    case SAVE_PHONE_NUMBER_ERROR:
      return { ...state, saveContactDetailsInProgress: false, savePhoneNumberError: payload };
    case SAVE_CONTACT_DETAILS_CLEAR:
      return {
        ...state,
        saveContactDetailsInProgress: false,
        saveEmailError: null,
        savePhoneNumberError: null,
        contactDetailsChanged: false,
      };

    case CHANGE_PASSWORD_REQUEST:
      return {
        ...state,
        changePasswordInProgress: true,
        changePasswordError: null,
        passwordChanged: false,
      };
    case CHANGE_PASSWORD_SUCCESS:
      return { ...state, changePasswordInProgress: false, passwordChanged: true };
    case CHANGE_PASSWORD_ERROR:
      return { ...state, changePasswordInProgress: false, changePasswordError: payload };
    case CHANGE_PASSWORD_CLEAR:
      return {
        ...state,
        changePasswordInProgress: false,
        changePasswordError: null,
        passwordChanged: false,
      };

    case RESET_PASSWORD_REQUEST:
      return { ...state, resetPasswordInProgress: true, resetPasswordError: null };
    case RESET_PASSWORD_SUCCESS:
      return { ...state, resetPasswordInProgress: false };
    case RESET_PASSWORD_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, resetPasswordInProgress: false, resetPasswordError: payload };

    case DELETE_ACCOUNT_REQUEST:
      return { ...state, deleteAccountInProgress: true, deleteAccountError: null };
    case DELETE_ACCOUNT_SUCCESS:
      return { ...state, deleteAccountInProgress: false };
    case DELETE_ACCOUNT_ERROR:
      return { ...state, deleteAccountInProgress: false, deleteAccountError: payload };

    case SAVE_LINKS_REQUEST:
      return { ...state, saveLinksInProgress: true, saveLinksError: null, linksSaved: false };
    case SAVE_LINKS_SUCCESS:
      return { ...state, saveLinksInProgress: false, linksSaved: true };
    case SAVE_LINKS_ERROR:
      return { ...state, saveLinksInProgress: false, saveLinksError: payload };
    case SAVE_LINKS_CLEAR:
      return { ...state, saveLinksInProgress: false, saveLinksError: null, linksSaved: false };

    case SAVE_VISIBILITY_REQUEST:
      return {
        ...state,
        saveVisibilityInProgress: true,
        saveVisibilityError: null,
        visibilitySaved: false,
      };
    case SAVE_VISIBILITY_SUCCESS:
      return { ...state, saveVisibilityInProgress: false, visibilitySaved: true };
    case SAVE_VISIBILITY_ERROR:
      return { ...state, saveVisibilityInProgress: false, saveVisibilityError: payload };
    case SAVE_VISIBILITY_CLEAR:
      return {
        ...state,
        saveVisibilityInProgress: false,
        saveVisibilityError: null,
        visibilitySaved: false,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const saveContactDetailsRequest = () => ({ type: SAVE_CONTACT_DETAILS_REQUEST });
export const saveContactDetailsSuccess = () => ({ type: SAVE_CONTACT_DETAILS_SUCCESS });
export const saveEmailError = error => ({ type: SAVE_EMAIL_ERROR, payload: error, error: true });
export const savePhoneNumberError = error => ({
  type: SAVE_PHONE_NUMBER_ERROR,
  payload: error,
  error: true,
});
export const saveContactDetailsClear = () => ({ type: SAVE_CONTACT_DETAILS_CLEAR });

export const changePasswordRequest = () => ({ type: CHANGE_PASSWORD_REQUEST });
export const changePasswordSuccess = () => ({ type: CHANGE_PASSWORD_SUCCESS });
export const changePasswordError = error => ({
  type: CHANGE_PASSWORD_ERROR,
  payload: error,
  error: true,
});
export const changePasswordClear = () => ({ type: CHANGE_PASSWORD_CLEAR });

export const resetPasswordRequest = () => ({ type: RESET_PASSWORD_REQUEST });
export const resetPasswordSuccess = () => ({ type: RESET_PASSWORD_SUCCESS });
export const resetPasswordError = e => ({ type: RESET_PASSWORD_ERROR, error: true, payload: e });

export const deleteAccountRequest = () => ({ type: DELETE_ACCOUNT_REQUEST });
export const deleteAccountSuccess = () => ({ type: DELETE_ACCOUNT_SUCCESS });
export const deleteAccountError = error => ({
  type: DELETE_ACCOUNT_ERROR,
  payload: error,
  error: true,
});

export const saveLinksRequest = () => ({ type: SAVE_LINKS_REQUEST });
export const saveLinksSuccess = () => ({ type: SAVE_LINKS_SUCCESS });
export const saveLinksError = e => ({ type: SAVE_LINKS_ERROR, payload: e, error: true });
export const saveLinksClear = () => ({ type: SAVE_LINKS_CLEAR });

export const saveVisibilityRequest = () => ({ type: SAVE_VISIBILITY_REQUEST });
export const saveVisibilitySuccess = () => ({ type: SAVE_VISIBILITY_SUCCESS });
export const saveVisibilityError = e => ({ type: SAVE_VISIBILITY_ERROR, payload: e, error: true });
export const saveVisibilityClear = () => ({ type: SAVE_VISIBILITY_CLEAR });

// ================ Thunks ================ //

const intercomUpdatePayload = user => {
  const categoryString = user?.attributes?.profile?.publicData?.categories?.join(',');
  const experienceString = user?.attributes?.profile?.publicData?.experienceKeys?.join(',');

  return {
    email: user.attributes.email,
    firstName: user.attributes.profile.firstName,
    lastName: user.attributes.profile.lastName,
    phone: user.attributes.profile.protectedData?.phoneNumber,
    textMessageNotifications: user.attributes.profile.protectedData?.textMessageNotifications,
    vatNumber: user.attributes.profile.protectedData?.vat_number,
    categories: categoryString,
    experience: experienceString,
    accountRole: user.attributes.profile.publicData?.account_role,
    emailVerified: user.attributes.emailVerified,
    lookingFor: Array.isArray(user.attributes.profile.protectedData?.lookingFor)
      ? user.attributes.profile.protectedData.lookingFor.join(',')
      : user.attributes.profile.protectedData?.lookingFor,
    notificationLanguage: user.attributes.profile.privateData?.notificationLanguage,
  };
};

const requestSavePhoneNumber = params => (dispatch, getState, sdk) => {
  const phoneNumber = params.phoneNumber.includes('+')
    ? params.phoneNumber
    : '+' + params.phoneNumber;
  const textMessageNotifications = params.textMessageNotifications;

  return sdk.currentUser
    .updateProfile(
      { protectedData: { phoneNumber, textMessageNotifications } },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      return entities[0];
    })
    .catch(e => {
      dispatch(savePhoneNumberError(storableError(e)));
      throw e;
    });
};

const requestSaveEmail = params => (dispatch, getState, sdk) => {
  const { email, currentPassword } = params;
  return sdk.currentUser
    .changeEmail(
      { email, currentPassword },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.changeEmail response');
      }
      return entities[0];
    })
    .catch(e => {
      dispatch(saveEmailError(storableError(e)));
      throw e;
    });
};

const saveEmail = params => (dispatch, getState, sdk) => {
  return dispatch(requestSaveEmail(params))
    .then(user => {
      dispatch(currentUserShowSuccess(user));
      dispatch(saveContactDetailsSuccess());
      // Contact attributes are written through the backend Intercom API.
      updateIntercomUser(intercomUpdatePayload(user));
      return user;
    })
    .catch(() => null);
};

const savePhoneNumber = params => (dispatch, getState, sdk) => {
  return dispatch(requestSavePhoneNumber(params))
    .then(user => {
      dispatch(currentUserShowSuccess(user));
      dispatch(saveContactDetailsSuccess());
      // Contact attributes are written through the backend Intercom API.
      updateIntercomUser(intercomUpdatePayload(user));
      return user;
    })
    .catch(() => null);
};

const saveEmailAndPhoneNumber = params => (dispatch, getState, sdk) => {
  const { email, phoneNumber, currentPassword, textMessageNotifications } = params;

  const promises = [
    dispatch(requestSaveEmail({ email, currentPassword })),
    dispatch(requestSavePhoneNumber({ phoneNumber, textMessageNotifications })),
  ];

  return Promise.all(promises)
    .then(values => {
      const saveEmailUser = values[0];
      const savePhoneNumberUser = values[1];

      const protectedData = savePhoneNumberUser.attributes.profile.protectedData;
      const phoneNumberMergeSource = { attributes: { profile: { protectedData } } };
      const currentUser = merge(saveEmailUser, phoneNumberMergeSource);

      // Contact attributes are written through the backend Intercom API.
      updateIntercomUser(intercomUpdatePayload(currentUser));
      dispatch(currentUserShowSuccess(currentUser));
      dispatch(saveContactDetailsSuccess());
    })
    .catch(() => null);
};

export const saveContactDetails = params => (dispatch, getState, sdk) => {
  dispatch(saveContactDetailsRequest());

  const {
    email,
    currentEmail,
    phoneNumber,
    textMessageNotifications,
    currentPhoneNumber,
    currentPassword,
    currentTextMessageNotifications,
  } = params;
  const emailChanged = email !== currentEmail;
  const phoneNumberChanged = phoneNumber !== currentPhoneNumber;
  const textMessageNotificationsChanged =
    textMessageNotifications !== currentTextMessageNotifications;

  if (emailChanged && (phoneNumberChanged || textMessageNotificationsChanged)) {
    return dispatch(
      saveEmailAndPhoneNumber({ email, currentPassword, phoneNumber, textMessageNotifications })
    );
  } else if (emailChanged) {
    return dispatch(saveEmail({ email, currentPassword }));
  } else if (phoneNumberChanged || textMessageNotificationsChanged) {
    return dispatch(savePhoneNumber({ phoneNumber, textMessageNotifications }));
  }
};

export const changePassword = params => (dispatch, getState, sdk) => {
  dispatch(changePasswordRequest());
  const { newPassword, currentPassword } = params;

  return sdk.currentUser
    .changePassword({ newPassword, currentPassword })
    .then(() => dispatch(changePasswordSuccess()))
    .catch(e => {
      dispatch(changePasswordError(storableError(e)));
      throw e;
    });
};

export const resetPassword = email => (dispatch, getState, sdk) => {
  dispatch(resetPasswordRequest());
  return sdk.passwordReset
    .request({ email })
    .then(() => dispatch(resetPasswordSuccess()))
    .catch(e => dispatch(resetPasswordError(storableError(e))));
};

export const deleteAccount = currentPassword => (dispatch, getState, sdk) => {
  dispatch(deleteAccountRequest());
  return deleteAccountApi({ currentPassword, deleteFromStripe: true })
    .then(() => {
      dispatch(deleteAccountSuccess());
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      dispatch(userLogout());
      return sdk.logout().catch(() => {
        // user already deleted
      });
    })
    .then(() => {
      if (typeof window === 'object') {
        // Clear the Intercom session for the deleted user. Without this, the
        // Messenger keeps the user's identity in its session cookie and
        // re-registers the contact in Intercom on the next page load, making
        // deleted users reappear. Mirror the logout flow in Topbar: shutdown,
        // then boot as an anonymous visitor.
        window.Intercom('shutdown');
        // Reboot as an anonymous visitor, reusing the settings injected by
        // index.html (window.intercomSettings) so we don't duplicate the app id.
        window.Intercom('boot', window.intercomSettings);
        window.location.href = '/';
      }
    })
    .catch(e => {
      // The local API helper keeps the HTTP status on apiResponse, which storableError drops;
      // without it a wrong password (403) can't be told apart from other failures.
      const status = e.status || (e.apiResponse && e.apiResponse.status);
      dispatch(deleteAccountError({ ...storableError(e), status }));
    });
};

export const saveLinks = params => (dispatch, getState, sdk) => {
  dispatch(saveLinksRequest());
  const { website = '', linkedIn = '' } = params;

  return sdk.currentUser
    .updateProfile(
      { protectedData: { website, linkedIn } },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      dispatch(currentUserShowSuccess(entities[0]));
      dispatch(saveLinksSuccess());
    })
    .catch(e => {
      dispatch(saveLinksError(storableError(e)));
    });
};

export const saveVisibility = params => (dispatch, getState, sdk) => {
  dispatch(saveVisibilityRequest());
  const { noIndexing, showAtMarketPlace, userId } = params;

  return sdk.currentUser
    .updateProfile(
      { publicData: { noIndexing: !!noIndexing } },
      {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      dispatch(currentUserShowSuccess(entities[0]));
      return updateShowAtMarketPlaceApi({ userId, showAtMarketPlace: !!showAtMarketPlace });
    })
    .then(() => {
      dispatch(saveVisibilitySuccess());
      // Refresh the user so metadata.showAtMarketPlace is up-to-date in Redux
      return dispatch(fetchCurrentUser());
    })
    .catch(e => {
      dispatch(saveVisibilityError(storableError(e)));
    });
};

// ================ Load data ================ //

export const loadData = () => fetchCurrentUser();
